import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { verboseLog, verboseWarn } from 'src/utils/config';

const FooterContainer = styled.footer`
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
  color: white;
  padding: 20px 0;
  margin-top: auto;
  text-align: center;
  font-size: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
`;

const VersionInfo = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  justify-content: center;
`;

const VersionItem = styled.div<{ isDefault?: boolean }>`
  background: ${props => props.isDefault ? 'rgba(255, 193, 7, 0.2)' : 'rgba(255, 255, 255, 0.1)'};
  padding: 8px 12px;
  border-radius: 6px;
  font-family: 'Courier New', monospace;
  font-size: 11px;
  border: ${props => props.isDefault ? '1px solid rgba(255, 193, 7, 0.3)' : 'none'};
`;

const Copyright = styled.div`
  opacity: 0.8;
`;

interface VersionData {
  SOFTWARE_VERSION: string;
  GIT_COMMIT_SHA: string;
  BUILD_DATE: string;
}

const Footer: React.FC = () => {
  const [versionData, setVersionData] = useState<VersionData | null>(null);
  const [isUsingDefaults, setIsUsingDefaults] = useState(false);

  useEffect(() => {
    // Check session storage first for cached version data
    const cachedVersionData = sessionStorage.getItem('versionData');
    const cacheTimestamp = sessionStorage.getItem('versionDataTimestamp');
    
    if (cachedVersionData && cacheTimestamp) {
      try {
        const parsedData = JSON.parse(cachedVersionData);
        const cacheAge = Date.now() - parseInt(cacheTimestamp);
        const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours; deploys are infrequent (1–2 months)
        
        // Only use cache if it's less than 24 hours old
        if (cacheAge < CACHE_DURATION) {
          setVersionData(parsedData);
          setIsUsingDefaults(false);
          verboseLog('Using cached version data:', parsedData);
          return; // Use cached data, skip API call
        } else {
          // Cache is too old, clear it
          verboseLog('Cache expired, fetching fresh data');
          sessionStorage.removeItem('versionData');
          sessionStorage.removeItem('versionDataTimestamp');
        }
      } catch (error) {
        verboseWarn('Failed to parse cached version data:', error);
        sessionStorage.removeItem('versionData');
        sessionStorage.removeItem('versionDataTimestamp');
      }
    }

    const deployMode = process.env.REACT_APP_DEPLOY_MODE;
    // Default to production mode if REACT_APP_DEPLOY_MODE is not set (deployed environment)
    const isProduction = deployMode === 'PROD' || !deployMode;
    
    verboseLog('Footer Debug:', {
      deployMode,
      isProduction,
      backendUrl: process.env.REACT_APP_BACKEND_URL
    });
    
    if (isProduction) {
      // Production mode: fetch from backend API
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2000);
      
      const fetchVersion = async () => {
        try {
          // In production, ensure we use the correct backend URL
          const baseUrl = process.env.REACT_APP_BACKEND_URL || 'https://llm.joexing.me/api/';
          const url = `${baseUrl}${baseUrl.endsWith('/') ? '' : '/'}version.txt`;
          console.log('Production mode: Fetching from', url);
          
          const response = await fetch(url, { 
            signal: controller.signal,
            headers: {
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            }
          });
          
          console.log('Response status:', response.status);
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          const text = await response.text();
          console.log('Response text:', text);
          
          const lines = text.split('\n').filter((line: string) => line.trim());
          const data: Partial<VersionData> = {};
          
          lines.forEach((line: string) => {
            const [key, value] = line.split('=');
            if (key && value) {
              data[key as keyof VersionData] = value;
            }
          });
          
          console.log('Parsed data:', data);
          setVersionData(data as VersionData);
          setIsUsingDefaults(false);
          
          // Cache the version data in session storage with timestamp
          sessionStorage.setItem('versionData', JSON.stringify(data));
          sessionStorage.setItem('versionDataTimestamp', Date.now().toString());
        } catch (error: any) {
          console.error('Production fetch error:', error);
          // Fallback to default values if backend is unavailable
          const fallbackData = {
            SOFTWARE_VERSION: '0.1.0',
            GIT_COMMIT_SHA: 'unknown',
            BUILD_DATE: 'unknown'
          };
          setVersionData(fallbackData);
          setIsUsingDefaults(true);
          
          // Cache fallback data with timestamp
          sessionStorage.setItem('versionData', JSON.stringify(fallbackData));
          sessionStorage.setItem('versionDataTimestamp', Date.now().toString());
        } finally {
          clearTimeout(timeoutId);
        }
      };
      
      fetchVersion();
    } else {
      // Local development mode: use git commands
      const getLocalGitInfo = async () => {
        try {
          // Get current commit SHA
          const commitResponse = await fetch('/api/git-commit', { 
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
          });
          const commitData = await commitResponse.json();
          
          // Get current branch
          const branchResponse = await fetch('/api/git-branch', { 
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
          });
          const branchData = await branchResponse.json();
          
          const localData = {
            SOFTWARE_VERSION: 'dev',
            GIT_COMMIT_SHA: commitData.commit || 'local-dev',
            BUILD_DATE: 'dev'
          };
          
          setVersionData(localData);
          setIsUsingDefaults(false);
          
          // Cache local data with timestamp
          sessionStorage.setItem('versionData', JSON.stringify(localData));
          sessionStorage.setItem('versionDataTimestamp', Date.now().toString());
        } catch (error: any) {
          // Fallback to default local values
          const fallbackData = {
            SOFTWARE_VERSION: 'dev',
            GIT_COMMIT_SHA: 'local-dev',
            BUILD_DATE: 'dev'
          };
          
          setVersionData(fallbackData);
          setIsUsingDefaults(true);
          
          // Cache fallback data with timestamp
          sessionStorage.setItem('versionData', JSON.stringify(fallbackData));
          sessionStorage.setItem('versionDataTimestamp', Date.now().toString());
        }
      };
      
      getLocalGitInfo();
    }
  }, []);

  const formatCommitSha = (sha: string) => {
    return sha ? sha.substring(0, 8) : 'unknown';
  };

  return (
    <FooterContainer>
      <FooterContent>
        <Copyright>
          © {new Date().getFullYear()} Joe Xing – Original concept and intellectual property.
        </Copyright>
        
        {versionData && (
          <VersionInfo>
            <VersionItem isDefault={isUsingDefaults}>
              Version: {versionData.SOFTWARE_VERSION || 'unknown'}
            </VersionItem>
            <VersionItem isDefault={isUsingDefaults}>
              Commit: {formatCommitSha(versionData.GIT_COMMIT_SHA)}
            </VersionItem>
            <VersionItem isDefault={isUsingDefaults}>
              Built: {versionData.BUILD_DATE || 'unknown'}
            </VersionItem>
            {isUsingDefaults && (
              <VersionItem isDefault={true} style={{ fontSize: '10px', opacity: 0.8 }}>
                ⚠️ {process.env.REACT_APP_DEPLOY_MODE === 'PROD' ? 'Backend offline' : 'Git info unavailable'}
              </VersionItem>
            )}
          </VersionInfo>
        )}
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer; 